import { create } from 'zustand';
import { apiClient, toSnakeCase } from '@/lib/apiClient';
import { logger } from '@/lib/logger';
import type {
  InterviewStartResponse,
  InterviewChatResponse,
} from '@/types/interview';

// ── Types ──────────────────────────────────────────────────────

export interface InterviewMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
  metadata?: {
    entities?: ExtractedEntity[];
    reasoningTraceId?: string;
  };
}

export interface ExtractedEntity {
  name: string;
  /** OntologyMetaType or any domain-specific type from extraction */
  type: string;
  description: string;
  status: string;
}

export interface ExtractedRelationship {
  source: string;
  target: string;
  type: string;
  description: string;
}

export interface InterviewSession {
  id: string;
  title: string;
  topic?: string;
  templateId?: string;
  status: string;
  extractedEntityCount: number;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

/** Lightweight report summary shown as child of a session */
export interface SessionReport {
  id: string;
  summary?: string;
  status: string;
  entityCount: number;
  relationshipCount: number;
  createdAt?: string;
}

/** Which view is active in the unified interviewer page */
export type InterviewViewMode = 'chat' | 'report';

interface InterviewState {
  // Session list
  sessions: InterviewSession[];
  sessionsLoading: boolean;

  // Active session
  activeSessionId: string | null;
  messages: InterviewMessage[];
  messagesLoading: boolean;

  // Streaming
  isStreaming: boolean;
  streamingContent: string;
  /** True between last token and the entities event — backend is extracting. */
  isExtractingEntities: boolean;

  // Extracted data from current turn
  lastExtractedEntities: ExtractedEntity[];
  lastExtractedRelationships: ExtractedRelationship[];
  allExtractedEntities: ExtractedEntity[];

  // Reports per session  (sessionId → reports)
  sessionReports: Record<string, SessionReport[]>;
  reportsLoading: boolean;

  // Unified view mode
  viewMode: InterviewViewMode;
  activeReportId: string | null;

  // Actions
  loadSessions: (workspaceId: string, token?: string | null) => Promise<void>;
  loadSession: (sessionId: string, token?: string | null) => Promise<void>;
  loadSessionReports: (workspaceId: string, token?: string | null) => Promise<void>;
  startSession: (
    workspaceId: string,
    userId: string,
    title?: string,
    topic?: string,
    templateId?: string,
    token?: string | null,
  ) => Promise<string>;
  sendMessage: (
    sessionId: string,
    workspaceId: string,
    message: string,
    topic?: string,
    token?: string | null,
  ) => Promise<void>;
  sendMessageStream: (
    sessionId: string,
    workspaceId: string,
    message: string,
    topic?: string,
    token?: string | null,
  ) => Promise<void>;
  completeSession: (
    sessionId: string,
    workspaceId: string,
    userId: string,
    userName: string,
    token?: string | null,
  ) => Promise<void>;
  deleteSession: (sessionId: string, token?: string | null) => Promise<void>;
  regenerateReport: (
    reportId: string,
    sessionId: string,
    workspaceId: string,
    userId: string,
    userName: string,
    token?: string | null,
  ) => Promise<void>;
  setActiveSessionId: (id: string | null) => void;
  setViewMode: (mode: InterviewViewMode, reportId?: string | null) => void;
  clearMessages: () => void;
  restoreCheckpoint: (sessionId: string, messageId: string, token?: string | null) => Promise<void>;
  /** @deprecated use restoreCheckpoint */
  undoLastTurn: (sessionId: string, token?: string | null) => Promise<void>;

  // Realtime streaming
  /** ID of the assistant message currently being built by realtime tokens. */
  realtimeStreamingId: string | null;
  /** ID of the user message created from voice transcript (live-updated while transcribing). */
  realtimeUserMessageId: string | null;
  /**
   * Add a user message whose content comes from the Realtime API
   * voice transcript (called as soon as the first transcript delta arrives).
   */
  addRealtimeUserMessage: (transcript: string) => void;
  /**
   * Live-update the content of the pending user voice message while
   * transcription deltas stream in.
   */
  updateRealtimeUserMessageText: (text: string) => void;
  /**
   * Push an empty assistant message placeholder and set isStreaming.
   * Must be called before the first appendRealtimeToken() of a turn.
   */
  startRealtimeResponse: () => void;
  /** Append one response.text.delta token to the streaming message. */
  appendRealtimeToken: (delta: string) => void;
  /**
   * Finalise the streamed assistant message (response.done).
   * Populates metadata with extracted entities and clears streaming state.
   */
  completeRealtimeResponse: (
    entities?: ExtractedEntity[],
    relationships?: ExtractedRelationship[],
  ) => void;
  /**
   * Back-fill entities on the last completed realtime assistant message.
   * Called after the context-sync returns, which is after completeRealtimeResponse.
   */
  updateLastTurnEntities: (
    entities: ExtractedEntity[],
    relationships: ExtractedRelationship[],
  ) => void;

  // Complete session state
  isCompleting: boolean;
  isRegenerating: boolean;
  isUndoing: boolean;
  isRestoring: boolean;
}

// ── Store ──────────────────────────────────────────────────────

export const useInterviewStore = create<InterviewState>()((set, get) => ({
  sessions: [],
  sessionsLoading: false,
  activeSessionId: null,
  messages: [],
  messagesLoading: false,
  isStreaming: false,
  streamingContent: '',
  isExtractingEntities: false,
  lastExtractedEntities: [],
  lastExtractedRelationships: [],
  allExtractedEntities: [],
  isCompleting: false,
  isRegenerating: false,
  isUndoing: false,
  isRestoring: false,
  sessionReports: {},
  reportsLoading: false,
  viewMode: 'chat' as InterviewViewMode,
  activeReportId: null,
  realtimeStreamingId: null,
  realtimeUserMessageId: null,

  loadSessions: async (workspaceId, token) => {
    set({ sessionsLoading: true });
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const resp = await fetch(
        `/api/dotnet/interviews/sessions?workspaceId=${workspaceId}`,
        { headers },
      );
      if (!resp.ok) throw new Error('Failed to load sessions');
      const data = await resp.json();
      set({ sessions: data, sessionsLoading: false });
    } catch {
      set({ sessionsLoading: false });
    }
  },

  loadSession: async (sessionId, token) => {
    set({ messagesLoading: true, activeSessionId: sessionId });
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const resp = await fetch(`/api/dotnet/interviews/sessions/${sessionId}`, {
        headers,
      });
      if (!resp.ok) throw new Error('Failed to load session');
      const data = await resp.json();

      const messages: InterviewMessage[] = (data.messages ?? []).map(
        (m: any) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          createdAt: m.createdAt,
          metadata: m.metadataJson ? JSON.parse(m.metadataJson) : undefined,
        }),
      );

      // Collect all entities from message metadata
      const allEntities: ExtractedEntity[] = [];
      for (const msg of messages) {
        if (msg.metadata?.entities) {
          allEntities.push(...msg.metadata.entities);
        }
      }

      set({
        messages,
        messagesLoading: false,
        allExtractedEntities: allEntities,
      });
    } catch {
      set({ messagesLoading: false });
    }
  },

  startSession: async (workspaceId, userId, title, topic, templateId, token) => {
    const data = await apiClient.post<InterviewStartResponse>(
      '/api/python/interview/sessions/start',
      {
        token,
        body: {
          workspaceId,
          userId,
          title: title || undefined,
          topic: topic || undefined,
          templateId: templateId || undefined,
        },
      },
    );

    const openingMessage: InterviewMessage = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: data.openingMessage,
      createdAt: new Date().toISOString(),
    };

    set({
      activeSessionId: data.sessionId,
      messages: [openingMessage],
      allExtractedEntities: [],
      lastExtractedEntities: [],
      lastExtractedRelationships: [],
    });

    // Refresh session list
    get().loadSessions(workspaceId, token);

    return data.sessionId;
  },

  sendMessage: async (sessionId, workspaceId, message, topic, token) => {
    const userMsg: InterviewMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: message,
      createdAt: new Date().toISOString(),
    };

    set((s) => ({ messages: [...s.messages, userMsg], isStreaming: true }));

    try {
      const data = await apiClient.post<InterviewChatResponse>(
        '/api/python/interview/chat',
        {
          token,
          body: {
            sessionId,
            workspaceId,
            userMessage: message,
            topic: topic || undefined,
          },
        },
      );

      const entities = (data.entities ?? []) as ExtractedEntity[];
      const relationships = (data.relationships ?? []) as ExtractedRelationship[];

      const assistantMsg: InterviewMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.response,
        createdAt: new Date().toISOString(),
        metadata: {
          entities: entities,
          reasoningTraceId: data.reasoningTraceId,
        },
      };

      set((s) => ({
        messages: [...s.messages, assistantMsg],
        isStreaming: false,
        lastExtractedEntities: entities,
        lastExtractedRelationships: relationships,
        allExtractedEntities: [
          ...s.allExtractedEntities,
          ...entities,
        ],
      }));
    } catch {
      set({ isStreaming: false });
    }
  },

  sendMessageStream: async (sessionId, workspaceId, message, topic, token) => {
    const userMsg: InterviewMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: message,
      createdAt: new Date().toISOString(),
    };

    set((s) => ({
      messages: [...s.messages, userMsg],
      isStreaming: true,
      streamingContent: '',
    }));

    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      // Streaming cannot use apiClient (needs raw response body reader),
      // but we still transform request body to snake_case for Python API
      const resp = await fetch('/api/python/interview/chat/stream', {
        method: 'POST',
        headers,
        body: JSON.stringify(
          toSnakeCase({
            sessionId,
            workspaceId,
            userMessage: message,
            topic: topic || undefined,
          }),
        ),
      });

      if (!resp.ok) throw new Error('Stream request failed');

      const reader = resp.body?.getReader();
      if (!reader) throw new Error('No reader');

      const decoder = new TextDecoder();
      let fullContent = '';
      let entities: ExtractedEntity[] = [];
      let relationships: ExtractedRelationship[] = [];
      // Buffer for partial SSE lines that span chunk boundaries
      let lineBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Append decoded chunk to carry-over buffer, then split on newlines.
        // A data: line can be split across two TCP chunks (especially large
        // entity JSON), so we keep the last incomplete fragment in lineBuffer.
        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split('\n');
        // All lines except the last are complete; the last may be partial.
        lineBuffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          try {
            const event = JSON.parse(jsonStr) as { type: string; content?: string; entities?: ExtractedEntity[]; relationships?: ExtractedRelationship[] };
            if (event.type === 'token') {
              fullContent += event.content ?? '';
              set({ streamingContent: fullContent });
            } else if (event.type === 'extracting') {
              set({ isExtractingEntities: true });
            } else if (event.type === 'entities') {
              entities = event.entities ?? [];
              relationships = event.relationships ?? [];
            }
            // 'done' event needs no action
          } catch {
            // Skip malformed events
          }
        }
      }
      // Flush any remaining complete line in the buffer (no trailing newline)
      if (lineBuffer.startsWith('data: ')) {
        try {
          const event = JSON.parse(lineBuffer.slice(6).trim()) as { type: string; entities?: ExtractedEntity[]; relationships?: ExtractedRelationship[] };
          if (event.type === 'entities') {
            entities = event.entities ?? [];
            relationships = event.relationships ?? [];
          }
        } catch { /* ignore */ }
      }

      const assistantMsg: InterviewMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: fullContent,
        createdAt: new Date().toISOString(),
        metadata: {
          entities: entities.length > 0 ? entities : undefined,
        },
      };

      set((s) => ({
        messages: [...s.messages, assistantMsg],
        isStreaming: false,
        streamingContent: '',
        isExtractingEntities: false,
        lastExtractedEntities: entities,
        lastExtractedRelationships: relationships,
        allExtractedEntities: [...s.allExtractedEntities, ...entities],
      }));
    } catch {
      set({ isStreaming: false, streamingContent: '', isExtractingEntities: false });
    }
  },

  completeSession: async (sessionId, workspaceId, userId, userName, token) => {
    set({ isCompleting: true });
    try {
      await apiClient.post('/api/python/interview/sessions/complete', {
        token,
        body: { sessionId, workspaceId, userId, userName },
      });

      // Update session status locally
      set((s) => ({
        sessions: s.sessions.map((ses) =>
          ses.id === sessionId ? { ...ses, status: 'Completed' } : ses,
        ),
        isCompleting: false,
      }));

      // Refresh session list
      get().loadSessions(workspaceId, token);
    } catch (err) {
      set({ isCompleting: false });
      throw err;
    }
  },

  deleteSession: async (sessionId, token) => {
    const headers: HeadersInit = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    await fetch(`/api/dotnet/interviews/sessions/${sessionId}`, {
      method: 'DELETE',
      headers,
    });

    set((s) => ({
      sessions: s.sessions.filter((ses) => ses.id !== sessionId),
      activeSessionId:
        s.activeSessionId === sessionId ? null : s.activeSessionId,
      messages: s.activeSessionId === sessionId ? [] : s.messages,
    }));
  },

  setActiveSessionId: (id) => set({ activeSessionId: id, viewMode: 'chat', activeReportId: null }),

  regenerateReport: async (reportId, sessionId, workspaceId, userId, userName, token) => {
    set({ isRegenerating: true });
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      // 1. Delete the existing report + nodes from Neo4j
      await fetch(`/api/dotnet/steward/reports/${encodeURIComponent(reportId)}`, {
        method: 'DELETE',
        headers,
      });

      // 2. Re-run complete_interview to regenerate
      await apiClient.post('/api/python/interview/sessions/complete', {
        token,
        body: { sessionId, workspaceId, userId, userName },
      });

      // 3. Refresh reports
      set({ isRegenerating: false });
      await get().loadSessionReports(workspaceId, token);
    } catch (err) {
      set({ isRegenerating: false });
      throw err;
    }
  },

  setViewMode: (mode, reportId) =>
    set({ viewMode: mode, activeReportId: reportId ?? null }),

  loadSessionReports: async (workspaceId, token) => {
    set({ reportsLoading: true });
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      // Load ALL reports for this workspace (no status filter)
      const url = `/api/dotnet/steward/reports?workspaceId=${encodeURIComponent(workspaceId)}`;
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`Failed to load reports: ${res.status}`);

      const data: Array<{
        id: string;
        summary?: string;
        status: string;
        entityCount: number;
        relationshipCount: number;
        sessionId?: string;
        createdAt?: string;
      }> = await res.json();

      // Group by sessionId
      const grouped: Record<string, SessionReport[]> = {};
      for (const r of data) {
        const sid = r.sessionId;
        if (!sid) continue;
        if (!grouped[sid]) grouped[sid] = [];
        grouped[sid].push({
          id: r.id,
          summary: r.summary,
          status: r.status,
          entityCount: r.entityCount,
          relationshipCount: r.relationshipCount,
          createdAt: r.createdAt,
        });
      }

      set({ sessionReports: grouped, reportsLoading: false });
    } catch {
      set({ reportsLoading: false });
    }
  },

  clearMessages: () =>
    set({
      messages: [],
      activeSessionId: null,
      allExtractedEntities: [],
      lastExtractedEntities: [],
      lastExtractedRelationships: [],
    }),

  restoreCheckpoint: async (sessionId, messageId, token) => {
    set({ isRestoring: true, isUndoing: true });
    try {
      // ── Local truncation — always runs, regardless of server result ────────
      // Messages created during a live session have client-side crypto.randomUUID()
      // IDs that the server never saw (server assigns its own IDs). For those turns
      // the server returns 404, but the local state should still be trimmed.
      set((s) => {
        const checkpointIdx = s.messages.findIndex((m) => m.id === messageId);
        const keptMsgs = checkpointIdx >= 0 ? s.messages.slice(0, checkpointIdx + 1) : s.messages;

        // Rebuild entity list from kept messages
        const keptEntities = keptMsgs
          .filter((m) => m.role === 'assistant' && m.metadata?.entities)
          .flatMap((m) => m.metadata!.entities!);
        const seen = new Set<string>();
        const dedupedEntities = keptEntities.filter((e) => {
          if (seen.has(e.name)) return false;
          seen.add(e.name);
          return true;
        });

        return {
          messages: keptMsgs,
          isRestoring: false,
          isUndoing: false,
          allExtractedEntities: dedupedEntities,
          lastExtractedEntities: [],
          lastExtractedRelationships: [],
        };
      });

      // ── Server-side truncation — best-effort ───────────────────────────────
      // Works when the session was loaded from the server (IDs are server-assigned).
      // 404 means the message wasn't in the DB with this ID (live-session UUID),
      // which is acceptable — local truncation above is sufficient in that case.
      const headers: HeadersInit = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const resp = await fetch(
        `/api/dotnet/interviews/sessions/${sessionId}/messages/restore/${messageId}`,
        { method: 'DELETE', headers },
      );
      if (!resp.ok && resp.status !== 404) {
        logger.warn('useInterviewStore', 'Server restore returned unexpected status', resp.status);
      }
    } catch {
      set({ isRestoring: false, isUndoing: false });
    }
  },

  undoLastTurn: async (sessionId, token) => {
    // Find the third-to-last message (the assistant message before the last user turn)
    const msgs = get().messages;
    // Pattern: ... [assistant_checkpoint] [user] [assistant_last]
    // We want to restore to the assistant message before the last user message
    if (msgs.length < 3) return;
    const lastUserIdx = msgs.map((m) => m.role).lastIndexOf('user');
    if (lastUserIdx < 1) return;
    const checkpointMsg = msgs[lastUserIdx - 1]; // assistant before the last user message
    if (!checkpointMsg) return;
    await get().restoreCheckpoint(sessionId, checkpointMsg.id, token);
  },

  // ── Realtime streaming actions ──────────────────────────────────────────

  addRealtimeUserMessage: (transcript) => {
    const id = crypto.randomUUID();
    const msg: InterviewMessage = {
      id,
      role: 'user',
      content: transcript,
      createdAt: new Date().toISOString(),
    };
    set((s) => ({ messages: [...s.messages, msg], realtimeUserMessageId: id }));
  },

  updateRealtimeUserMessageText: (text) => {
    set((s) => {
      if (!s.realtimeUserMessageId) return {};
      return {
        messages: s.messages.map((m) =>
          m.id === s.realtimeUserMessageId ? { ...m, content: text } : m,
        ),
      };
    });
  },

  startRealtimeResponse: () => {
    const id = crypto.randomUUID();
    const placeholder: InterviewMessage = {
      id,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
    };
    set((s) => ({
      messages: [...s.messages, placeholder],
      isStreaming: true,
      streamingContent: '',
      realtimeStreamingId: id,
    }));
  },

  appendRealtimeToken: (delta) => {
    set((s) => ({ streamingContent: s.streamingContent + delta }));
  },

  completeRealtimeResponse: (entities = [], relationships = []) => {
    set((s) => {
      const { realtimeStreamingId, streamingContent } = s;
      const updatedMessages = s.messages.map((m) =>
        m.id === realtimeStreamingId
          ? {
              ...m,
              content: streamingContent,
              metadata: entities.length > 0 ? { entities } : undefined,
            }
          : m,
      );
      return {
        messages: updatedMessages,
        isStreaming: false,
        streamingContent: '',
        realtimeStreamingId: null,
        realtimeUserMessageId: null,
        lastExtractedEntities: entities,
        lastExtractedRelationships: relationships,
        allExtractedEntities: [...s.allExtractedEntities, ...entities],
      };
    });
  },

  updateLastTurnEntities: (entities, relationships) => {
    // Find the most recent assistant message that has no entities yet and
    // back-fill them once the context-sync response returns.
    set((s) => {
      const lastAssistantIdx = [...s.messages].reverse().findIndex(
        (m) => m.role === 'assistant',
      );
      if (lastAssistantIdx === -1) return {};
      const realIdx = s.messages.length - 1 - lastAssistantIdx;
      const updatedMessages = s.messages.map((m, i) =>
        i === realIdx
          ? { ...m, metadata: { ...(m.metadata ?? {}), entities } }
          : m,
      );
      return {
        messages: updatedMessages,
        lastExtractedEntities: entities,
        lastExtractedRelationships: relationships,
        allExtractedEntities: [
          ...s.allExtractedEntities.filter(
            (e) => !entities.some((ne) => ne.name === e.name),
          ),
          ...entities,
        ],
      };
    });
  },
}));
